import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { get, set } from 'lodash';
import { FieldValues } from 'react-hook-form';
import { clsx } from 'clsx';

import { Utils, DateTools, TLocaleCode } from '@treezor/design-system';

import {
  IAttributesContext,
  IAttributeConfig,
  displayvalues,
  TDisplayValues,
  TAttributesContextWithChecked,
  TCheckedValues,
} from '@/features/wallets/store/models/wallet.model';
import { TOperationTranslations, TAmountWithDirection } from '@/features/wallets/store/models/walletOperation.model';

import { operationsZones, initialFlows } from '@/store/models/operation.model';

import headersConfig from './WalletOperationsConfig/WalletOpoerationsConfigHeaders.json';
import popinConfig from './WalletOperationsConfig/WalletOperationConfigPopin.json';
import filtersFormConfig from './WalletOperationsConfig/WalletOperationConfigFiltersForm.json';
import filtersGlobalConfig from './WalletOperationsConfig/WalletOperationConfigFiltersGlobal.json';
import filtersPayinsConfig from './WalletOperationsConfig/WalletOperationConfigFiltersPayins.json';
import filtersPayinsRefundConfig from './WalletOperationsConfig/WalletOperationConfigFiltersPayinsRefunds.json';
import filtersPayoutsConfig from './WalletOperationsConfig/WalletOperationConfigFiltersPayout.json';
import filtersCardTransactions from './WalletOperationsConfig/WalletOperationConfigFiltersCardTransactions.json';
import filtersChargeback from './WalletOperationsConfig/WalletOperationConfigFiltersChargeback.json';
import filtersPayoutsRefundConfig from './WalletOperationsConfig/WalletOperationConfigFiltersPayoutRefund.json';
import filtersTransfertsConfig from './WalletOperationsConfig/WalletOperationConfigFiltersTransfert.json';

export const getFormattedAmount = (
  amountInformation: TAmountWithDirection,
  locale: TLocaleCode,
  isOperationDeclined?: boolean,
) => {
  return (
    <div
      className={clsx(
        !amountInformation.direction || isOperationDeclined
          ? 'text-tag-gray'
          : amountInformation.direction === 'DEBIT'
            ? 'text-tag-red'
            : 'text-tag-green',
        'py-2 px-3 typo-body-medium-caption whitespace-nowrap',
      )}
    >
      {amountInformation.amount !== 0 && !isOperationDeclined && (
        <>{amountInformation.direction === 'DEBIT' ? '- ' : '+ '}</>
      )}
      <span className={isOperationDeclined ? 'line-through' : ''}>
        {Utils.currencyFormater(locale, amountInformation.amount, amountInformation.currency)}
      </span>
    </div>
  );
};

export const useOperationTable = () => {
  const formatAmountForCsv = useCallback((operation) => {
    const amountWithCurrency = `${operation.amount.amount / 100} ${operation.amount.currency === 'EUR' ? '€' : '$'}`;
    return operation.direction === 'DEBIT' ? `- ${amountWithCurrency}` : `+ ${amountWithCurrency}`;
  }, []);

  const getColumnContent = useCallback(
    (header: string, operation) => {
      switch (header) {
        case 'date.creation':
        case 'date.settlement':
        case 'metadata.refundDate':
          return {
            children: DateTools.format(get(operation, header), 'date-time') || ' - ',
          };
        case 'amount':
          return {
            children: formatAmountForCsv(operation),
          };
        default:
          return {
            children: get(operation, header) || '-',
          };
      }
    },
    [formatAmountForCsv],
  );

  return { getColumnContent };
};

export const useOperationLabelTranslations = (
  attributes: string[] = [],
  filter: (source: string) => string = (source) => source,
): TOperationTranslations => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      attributes.reduce<TOperationTranslations>((previousValue, currentValue) => {
        previousValue[currentValue] = t(`pages.currentProfiles.wallets.operations.${filter(currentValue)}`);
        return previousValue;
      }, {}),
    [t, attributes, filter],
  );
};

export function filterAttributestransaltionsKeys(source: any) {
  if (typeof source !== 'string') {
    return source;
  }
  Object.values(initialFlows).forEach((initialFlow) => {
    if (['payout.metadata.label', 'payoutRefund.metadata.label', 'transfer.metadata.label'].includes(source)) {
      source = `metadata.${source.replace(`metadata.`, '')}`;
    } else if (!['metadata.payout.label', 'metadata.payoutRefund.label', 'metadata.transfer.label'].includes(source)) {
      source = (source || '').replace(`${initialFlow}.`, '');
    }
  });
  return source;
}

function shouldDisplay(display: TDisplayValues, isCrypto) {
  return (
    display === displayvalues.ALL ||
    (display === displayvalues.NO_CRYPTO && isCrypto === false) ||
    (display === displayvalues.CRYPTO_ONLY && isCrypto === true)
  );
}

function filterAttributes(
  { isCrypto, type, initialFlow, fields = {}, select }: IAttributesContext,
  attributes: IAttributeConfig[],
): IAttributeConfig[] {
  let allFields = attributes;

  if (select) {
    allFields = attributes.filter((attribute) => {
      return (
        select.includes(attribute.name) &&
        shouldDisplay(attribute.display !== displayvalues.NONE ? attribute.display : displayvalues.ALL, isCrypto)
      );
    });
    allFields = allFields.map((attr) => ({ ...attr, display: displayvalues.ALL }));
  }

  return allFields.filter((attribute) => {
    if (attribute.childs) {
      attribute.childs = filterAttributes({ isCrypto, type, initialFlow, fields, select }, attribute.childs);
    }

    if (fields[attribute.name] !== undefined) {
      return fields[attribute.name];
    }

    if (shouldDisplay(attribute.display, isCrypto)) {
      return (
        (type && ((attribute.types && attribute.types.includes(type)) || !attribute.types)) ||
        (initialFlow &&
          ((attribute.initialFlows && attribute.initialFlows.includes(initialFlow)) || !attribute.initialFlows)) ||
        (!type && !initialFlow)
      );
    }
  });
}

export function setAttributeChecked(
  attribute,
  isChecked: TCheckedValues | ((attribute: IAttributeConfig) => boolean),
): IAttributeConfig & { isChecked: TCheckedValues } {
  if (typeof isChecked === 'function') {
    attribute.isChecked = isChecked(attribute);
    if (attribute.childs) {
      attribute.childs = attribute.childs.map((child) => {
        return {
          ...child,
          isChecked: isChecked(child),
        };
      });
    }
  } else {
    attribute.isChecked = isChecked;
    if (attribute.childs) {
      attribute.childs = attribute.childs.map((child) => {
        return {
          ...child,
          isChecked,
        };
      });
    }
  }
  return attribute;
}

export function getAttributes(config: TAttributesContextWithChecked): (IAttributeConfig & { isChecked: boolean })[];
export function getAttributes(config: IAttributesContext): IAttributeConfig[];
export function getAttributes(
  config: IAttributesContext | TAttributesContextWithChecked,
): IAttributeConfig[] | (IAttributeConfig & { isChecked: boolean })[] {
  const type = config.zone || config.type;
  let attributes: IAttributeConfig[] = [];
  switch (type) {
    case operationsZones.HEADER_TABLE:
      attributes = headersConfig as IAttributeConfig[];
      break;
    case operationsZones.FILTERS_FORM:
      attributes = filtersFormConfig as IAttributeConfig[];
      break;
    case operationsZones.FILTERS:
      attributes = [
        {
          name: operationsZones.GLOBAL,
          display: 'all',
          childs: filtersGlobalConfig as IAttributeConfig[],
        },
        {
          name: initialFlows.PAYIN,
          display: 'all',
          childs: filtersPayinsConfig as IAttributeConfig[],
        },
        {
          name: initialFlows.PAYIN_REFUND,
          display: 'all',
          childs: filtersPayinsRefundConfig as IAttributeConfig[],
        },
        {
          name: initialFlows.PAYOUT,
          display: 'all',
          childs: filtersPayoutsConfig as IAttributeConfig[],
        },
        {
          name: initialFlows.PAYOUT_REFUND,
          display: 'all',
          childs: filtersPayoutsRefundConfig as IAttributeConfig[],
        },
        {
          name: initialFlows.TRANSFERT,
          display: 'all',
          childs: filtersTransfertsConfig as IAttributeConfig[],
        },
        {
          name: initialFlows.CARD_TRANSACTION,
          display: 'all',
          childs: filtersCardTransactions as IAttributeConfig[],
        },
        {
          name: initialFlows.CHARGE_BACK,
          display: 'all',
          childs: filtersChargeback as IAttributeConfig[],
        },
      ];
      break;
    case operationsZones.POPIN:
      attributes = popinConfig as IAttributeConfig[];
      break;
    default:
      attributes = headersConfig as IAttributeConfig[];
  }

  return config.setChecked === undefined
    ? filterAttributes(config, attributes)
    : filterAttributes(config, attributes).map((attribute) => {
        return setAttributeChecked(attribute, config.setChecked);
      });
}

export function useAttributes(config: TAttributesContextWithChecked): (IAttributeConfig & { isChecked: boolean })[];
export function useAttributes(config: IAttributesContext): IAttributeConfig[];
export function useAttributes(
  config: IAttributesContext | TAttributesContextWithChecked,
): IAttributeConfig[] | (IAttributeConfig & { isChecked: boolean })[] {
  return getAttributes(config);
}

export function getAttribute(attributes: IAttributeConfig[], name: string): IAttributeConfig | undefined {
  const result: IAttributeConfig[] = [];
  (attributes || []).forEach((attribute: IAttributeConfig) => {
    if (attribute.name === name) {
      result.push(attribute);
    } else if (attribute.childs) {
      const childResult = getAttribute(attribute.childs, name);
      if (childResult) {
        result.push(childResult);
      }
    }
  });
  return result.length > 0 ? result[0] : undefined;
}

export function getAttributesValues(attributes: IAttributeConfig[], useDefault: boolean = false): FieldValues {
  const result: FieldValues = {};
  (attributes || []).forEach((attribute) => {
    if (attribute.childs) {
      result[attribute.name] = getAttributesValues(attribute.childs, useDefault);
    } else {
      result[attribute.name] = useDefault ? attribute.defaultValue : attribute.value;
    }
  });

  return result;
}

export function getCheckedAttributesValues(attributes: IAttributeConfig[]): FieldValues {
  const result: FieldValues = {};
  (attributes || []).forEach((attribute) => {
    result[attribute.name] = result[attribute.name] = attribute.isChecked === undefined ? false : attribute.isChecked;
    if (attribute.childs) {
      attribute.childs.forEach((child) => {
        result[child.name] = child.isChecked === undefined ? false : child.isChecked;
      });
    }
  });

  return result;
}

export function getAttributeKeys(attributes: (IAttributeConfig | string)[], level: number = 200): string[] {
  let result: string[] = [];
  level--;

  (attributes || []).forEach((attribute) => {
    if (typeof attribute === 'string') {
      return attribute;
    }
    if (attribute.childs && level > 0) {
      result = [...result, ...getAttributeKeys(attribute.childs)];
    }
    result.push(attribute.name);
  });

  return result;
}

export function useDefaultAttributesValues(attributes: IAttributeConfig[]): Record<string, string | boolean> {
  return getAttributesValues(attributes, true);
}

export function isAttributeConfig(data: any): data is IAttributeConfig {
  return !!(data as IAttributeConfig).name && !!(data as IAttributeConfig).display;
}

export function getFiltersFromNames(attributesNames: string[], config: IAttributesContext = {}) {
  return filterAttributes(
    config,
    [
      ...(filtersGlobalConfig as IAttributeConfig[]),
      ...(filtersPayinsConfig as IAttributeConfig[]),
      ...(filtersPayinsRefundConfig as IAttributeConfig[]),
      ...(filtersPayoutsConfig as IAttributeConfig[]),
      ...(filtersPayoutsRefundConfig as IAttributeConfig[]),
      ...(filtersTransfertsConfig as IAttributeConfig[]),
      ...(filtersCardTransactions as IAttributeConfig[]),
      ...(filtersChargeback as IAttributeConfig[]),
    ].filter((attribute) => attributesNames.includes(attribute.name)),
  );
}

export function setAttributesValue(attributes: IAttributeConfig[], values: Record<string, any>): IAttributeConfig[] {
  return attributes.map((attribute) => {
    Object.keys(values).forEach((key) => {
      if (key === attribute.name) {
        attribute.value = values[key];
      } else if (attribute.childs) {
        attribute.childs = setAttributesValue(attribute.childs, values);
      }
    });
    return attribute;
  });
}

export function setAttributeValue<T extends IAttributeConfig = IAttributeConfig>(
  attributes: T[],
  attributeName: string,
  path: string,
  value: any,
): T[] {
  (attributes || []).forEach((attribute) => {
    if (attribute.name === attributeName) {
      attribute = set(attribute, path, value);
    } else {
      attribute.childs.forEach((child) => {
        if (child.name === attributeName) {
          child = set(child, path, value);
        }
      });
    }
  });

  return attributes;
}

export function getParentAttribute<T extends IAttributeConfig = IAttributeConfig>(
  attributes: T[],
  attributeName: string,
): T | undefined {
  return attributes.find((attribute) => attribute.childs.map((child) => child.name).includes(attributeName));
}

export function getCheckedAttributes(attributes: IAttributeConfig[]): IAttributeConfig[] {
  const result = [];
  (attributes || []).forEach((attribute) => {
    if (attribute.isChecked) {
      result.push(attribute);
    }
    if (attribute.childs) {
      result.push(...getCheckedAttributes(attribute.childs));
    }
  });
  return result;
}
